import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CovanceGridColumn } from '../../../covance-ag-grid/covance-grid-column';
import { comparators } from '../../../core';
import { AdminEQueryEscalation } from '../models/admin-e-query-escalation';
import { dateTimeComparator, dateTimeRenderer } from '../../../covance-ag-grid';
import { GridOptions } from 'ag-grid-community';
import { AdminEQueryEscalationsStorage } from '../admin-e-query-escalations-storage';
import { Observable, Subject } from 'rxjs';
import { SearchFieldsValues } from './escalation-emails-filter/escalation-emails-filter.component';
import { switchMap, map, publishReplay, refCount, startWith } from 'rxjs/operators';
import { AdminEQueryEscalationsFilterResolver } from '../admin-e-query-escalations-filter-resolver';
import { FilterFieldValue, CovanceFilter } from '../../../covance-filter';
import { MdlDialogService, IMdlCustomDialogConfiguration } from '@angular-mdl/core';
import { NewEscalationDialogComponent } from './new-escalation-dialog/new-escalation-dialog.component';
import { EQueryEscalationLevel } from '../models/e-query-escalation-level';
import { EscalationRemovalButtonAgRenderer } from './escalation-removal-button-ag-renderer.component';
import { UserPermissionsStorage } from '../../../user-permissions/user-permissions-storage';

@Component({
    selector: 'admin-escalation-emails',
    template: require('./admin-escalation-emails.component.html'),
    styles: [require('./admin-escalation-emails.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminEscalationEmailsComponent {
    public readonly gridOptions: GridOptions = {
        floatingFilter: false,
        enableFilter: false,
    };
    public readonly filterGroup = 'admin-e-query-escalations';
    public readonly escalations$: Observable<AdminEQueryEscalation[]>;
    public readonly filterFields$: Observable<FilterFieldValue[]>;
    public readonly canUserAdd$: Observable<boolean>;
    public readonly columns$: Observable<CovanceGridColumn[]>;

    private readonly columns: CovanceGridColumn[] = [
        {
            field: 'email',
            headerName: 'User Email Address',
            comparator: comparators.stringComparator,
        },
        {
            field: 'escalationLevel',
            headerName: 'Escalation Level',
            comparator: comparators.stringComparator,
            valueGetter: (params: { data: AdminEQueryEscalation }) => {
                if (!params.data) {
                    return null;
                }
                return EQueryEscalationLevel[params.data.escalationLevel];
            }
        },
        {
            field: 'escalationAttempt',
            headerName: 'Escalation Attempt',
            comparator: comparators.numberComparator,
        },
        {
            field: 'eQueryId',
            headerName: 'eQueryId',
            comparator: comparators.numberComparator,
        },
        {
            field: 'accessionNumber',
            headerName: 'Accession',
            comparator: comparators.mixedStringComparator,
        },
        {
            field: 'escalationSentTime',
            headerName: 'Escalation Sent Time',
            comparator: dateTimeComparator,
            valueGetter: (params: { data: AdminEQueryEscalation }) => {
                if (!params.data) {
                    return null;
                }
                return dateTimeRenderer({ value: params.data.escalationSentTime });
            },
        },
    ];
    private readonly searchFieldValues$ = new Subject<SearchFieldsValues>();

    constructor(
        escalationEmailsStorage: AdminEQueryEscalationsStorage,
        filterResolver: AdminEQueryEscalationsFilterResolver,
        filterService: CovanceFilter,
        private readonly dialogService: MdlDialogService,
        userPermissionsStorage: UserPermissionsStorage,
    ) {
        const filterDefs = filterResolver.filterDefinitions();

        const filterFields$ = this.searchFieldValues$.pipe(
            map(values => {
                filterService.setFiltersItems([], filterDefs);
                return { fields: filterDefs, values };
            }),
            publishReplay(1),
            refCount(),
        );

        this.filterFields$ = filterFields$.pipe(map(x => x.fields));

        const filterFlow$ = filterFields$
            .pipe(
                switchMap(({ fields, values }) => filterService.listen(fields).pipe(map(() => ({ fields, values })))),
                publishReplay(1),
                refCount()
            );

        this.escalations$ = filterFlow$
            .pipe(
                switchMap(({ fields, values }) =>
                    escalationEmailsStorage.getEscalations(values, fields)
                ),
                publishReplay(1),
                refCount(),
                startWith([]),
            );

        const canAdministrate$ = userPermissionsStorage.userPermissions.pipe(
            map(permissions => permissions.has('EQueryAdministration')),
        );

        this.canUserAdd$ = canAdministrate$;
        this.columns$ = this.getGridColumns(canAdministrate$);
    }

    public onSearch(values: SearchFieldsValues) {
        this.searchFieldValues$.next(values);
    }

    public addEscalation() {
        const configDialog: IMdlCustomDialogConfiguration = {
            component: NewEscalationDialogComponent,
            isModal: true,
            clickOutsideToClose: true,
        };

        this.dialogService.showCustomDialog(configDialog);
    }

    private getGridColumns(canAdministrate: Observable<boolean>): Observable<CovanceGridColumn[]> {
        return canAdministrate.pipe(
            map(canRemove => {
                const removalColumn: CovanceGridColumn = {
                    colId: 'removal',
                    headerName: 'Remove User From Escalation List',
                    cellRendererFramework: EscalationRemovalButtonAgRenderer,
                    suppressSorting: true,
                    noExport: true,
                    suppressMenu: true,
                    suppressFilter: true,
                };
                return canRemove
                    ? [...this.columns, removalColumn]
                    : this.columns;
            }),
            publishReplay(1),
            refCount(),
        );
    }
}
